import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';

const SITE_NAME = 'St. Andrews World School';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://sawsindirapuram.com').replace(/\/+$/, '');
const DEFAULT_DESC =
  'St. Andrews World School, Indirapuram is a premier CISCE/ICSE affiliated school in Ghaziabad offering holistic education, smart classrooms, sports & modern laboratories. Admissions Open 2027-28.';
const DEFAULT_KEYWORDS =
  'best school in Indirapuram, ICSE school in Indirapuram, top school in Ghaziabad, St. Andrews World School Indirapuram, school admissions 2027-28';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const SEO = ({
  pageSlug,
  title,
  description,
  keywords,
  ogImage,
  canonical,
  noIndex,
  schema,
}) => {
  const location = useLocation();
  const [apiSeo, setApiSeo] = useState(null);

  // Compute clean slug from prop or current pathname
  const currentSlug = (pageSlug || location.pathname.replace(/^\/+|\/+$/g, '') || 'home').toLowerCase();

  useEffect(() => {
    let active = true;
    if (currentSlug) {
      API.get(`/seo/${currentSlug}`)
        .then((res) => {
          if (active && res.data?.data) {
            setApiSeo(res.data.data);
          }
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [currentSlug]);

  // Priority: Direct Props -> Backend API SEO -> Default Constants
  const effectiveTitle =
    apiSeo?.meta_title ||
    (title
      ? (title.toLowerCase().includes('st. andrews') ? title : `${title} | ${SITE_NAME}`)
      : `${SITE_NAME} | Best School in Indirapuram`);

  const effectiveDesc = apiSeo?.meta_description || description || DEFAULT_DESC;

  const effectiveKeywords = apiSeo?.meta_keywords || keywords || DEFAULT_KEYWORDS;

  let effectiveImage = DEFAULT_IMAGE;
  if (apiSeo?.og_image) {
    effectiveImage = apiSeo.og_image.startsWith('http')
      ? apiSeo.og_image
      : `${UPLOADS_URL}/${apiSeo.og_image.replace(/^\/+/, '')}`;
  } else if (ogImage) {
    effectiveImage = ogImage.startsWith('http')
      ? ogImage
      : `${SITE_URL}/${ogImage.replace(/^\/+/, '')}`;
  }

  const effectiveCanonical =
    apiSeo?.canonical_url ||
    canonical ||
    `${SITE_URL}${location.pathname === '/' ? '' : location.pathname}`;

  const isNoIndex = apiSeo?.no_index !== undefined ? !!apiSeo.no_index : !!noIndex;

  // Build standard rich JSON-LD structured data
  const baseSchoolSchema = {
    '@context': 'https://schema.org',
    '@type': 'School',
    name: 'St. Andrews World School, Indirapuram',
    alternateName: 'SAWS Indirapuram',
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    image: effectiveImage,
    description: DEFAULT_DESC,
    telephone: '+91-9311668860',
    email: 'info@standrewsworldschool.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'PS-5, Shakti Khand IV, Indirapuram',
      addressLocality: 'Ghaziabad',
      addressRegion: 'Uttar Pradesh',
      postalCode: '201014',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '28.6415',
      longitude: '77.3714',
    },
    sameAs: [
      'https://www.facebook.com/standrewsworldschool',
      'https://www.instagram.com/standrewsworldschool',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      ...(location.pathname !== '/'
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: title || currentSlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              item: effectiveCanonical,
            },
          ]
        : []),
    ],
  };

  const schemaArray = [
    baseSchoolSchema,
    breadcrumbSchema,
    ...(Array.isArray(schema) ? schema : schema ? [schema] : []),
  ];

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{effectiveTitle}</title>
      <meta name="title" content={effectiveTitle} />
      <meta name="description" content={effectiveDesc} />
      {effectiveKeywords && <meta name="keywords" content={effectiveKeywords} />}
      <link rel="canonical" href={effectiveCanonical} />
      <meta name="robots" content={isNoIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="author" content={SITE_NAME} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={effectiveCanonical} />
      <meta property="og:title" content={effectiveTitle} />
      <meta property="og:description" content={effectiveDesc} />
      <meta property="og:image" content={effectiveImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={effectiveCanonical} />
      <meta name="twitter:title" content={effectiveTitle} />
      <meta name="twitter:description" content={effectiveDesc} />
      <meta name="twitter:image" content={effectiveImage} />

      {/* JSON-LD Structured Data */}
      {schemaArray.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
